import { useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { Calendar as CalendarIcon, Info, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toDateOnlyString(d: Date) {
  return format(d, 'yyyy-MM-dd');
}

function parseDateOnly(value: unknown): Date | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  try {
    const d = parseISO(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  } catch {
    return undefined;
  }
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

type DayKey = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';
const DAY_KEYS: DayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DAY_LABEL: Record<DayKey, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

function getDayKeyFromDate(d: Date): DayKey {
  return DAY_KEYS[d.getDay()] ?? 'mon';
}

function parseHHmmToMinutes(value: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const hh = Number.parseInt(m[1], 10);
  const mm = Number.parseInt(m[2], 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  if (hh < 0 || hh > 23) return null;
  if (mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
}

export function AppointmentConfigEditor({
  config,
  onPatchConfig,
  squarePaymentFields,
  crossFieldRescheduleWarning,
}: {
  config: Record<string, unknown>;
  onPatchConfig: (patch: Record<string, unknown>) => void;
  /** Same-form Square `payment` fields to store reschedule charges (`reschedulePaymentFieldId`). */
  squarePaymentFields: { id: string; label: string }[];
  /** When multiple appointment fields charge a fee, they must share one payment field id. */
  crossFieldRescheduleWarning?: string | null;
}) {
  // Ensure config always contains backend-required keys so publish cannot fail due to missing defaults.
  useEffect(() => {
    const patch: Record<string, unknown> = {};
    if (typeof config.slotDurationMinutes !== 'number') patch.slotDurationMinutes = 30;
    if (typeof config.slotIntervalMinutes !== 'number') patch.slotIntervalMinutes = 30;
    if (!isPlainObject(config.weeklySchedule)) {
      patch.weeklySchedule = {
        sun: { enabled: false, ranges: [] },
        mon: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
        tue: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
        wed: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
        thu: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
        fri: { enabled: true, ranges: [{ start: '09:00', end: '17:00' }] },
        sat: { enabled: false, ranges: [] },
      };
    } else {
      const ws = config.weeklySchedule as Record<string, unknown>;
      const ensureDay = (k: DayKey, enabled: boolean) => {
        if (!isPlainObject(ws[k])) {
          (patch.weeklySchedule ??= { ...ws }) as any;
          (patch.weeklySchedule as any)[k] = { enabled, ranges: enabled ? [{ start: '09:00', end: '17:00' }] : [] };
        }
      };
      ensureDay('sun', false);
      ensureDay('mon', true);
      ensureDay('tue', true);
      ensureDay('wed', true);
      ensureDay('thu', true);
      ensureDay('fri', true);
      ensureDay('sat', false);
    }
    if (!isPlainObject(config.capacity)) {
      patch.capacity = { mode: 'one_on_one', maxAttendeesPerSlot: 1 };
    } else {
      const cap = config.capacity as Record<string, unknown>;
      const mode = cap.mode === 'group' ? 'group' : 'one_on_one';
      const max = typeof cap.maxAttendeesPerSlot === 'number' ? cap.maxAttendeesPerSlot : 1;
      if (cap.mode !== mode || cap.maxAttendeesPerSlot !== max) {
        patch.capacity = { ...cap, mode, maxAttendeesPerSlot: mode === 'one_on_one' ? 1 : max };
      }
    }
    if (Object.keys(patch).length > 0) onPatchConfig(patch);
    // Only run when required keys are missing/changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typeof config.slotDurationMinutes,
    typeof config.slotIntervalMinutes,
    JSON.stringify(config.weeklySchedule),
    JSON.stringify(config.capacity),
  ]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const bookingWindow = isPlainObject(config.bookingWindow) ? (config.bookingWindow as Record<string, unknown>) : {};
  const bookingMode = typeof bookingWindow.mode === 'string' ? String(bookingWindow.mode) : 'rolling';
  const rollingDaysAhead =
    typeof bookingWindow.rollingDaysAhead === 'number' && Number.isFinite(bookingWindow.rollingDaysAhead)
      ? Math.max(1, Math.min(730, Number(bookingWindow.rollingDaysAhead)))
      : 365;

  const absoluteStart = parseDateOnly(bookingWindow.startDate);
  const absoluteEnd = parseDateOnly(bookingWindow.endDate);

  const horizonEnd =
    bookingMode === 'absolute' && absoluteEnd ? absoluteEnd : addDays(today, bookingMode === 'rolling' ? rollingDaysAhead : 365);

  const duration =
    typeof config.slotDurationMinutes === 'number' ? Math.max(5, Math.min(1440, Number(config.slotDurationMinutes))) : 30;
  const interval =
    typeof config.slotIntervalMinutes === 'number' ? Math.max(5, Math.min(1440, Number(config.slotIntervalMinutes))) : 30;

  const capacity = isPlainObject(config.capacity) ? (config.capacity as Record<string, unknown>) : {};
  const capacityMode = typeof capacity.mode === 'string' ? String(capacity.mode) : 'one_on_one';
  const maxAttendees =
    capacityMode === 'one_on_one'
      ? 1
      : typeof capacity.maxAttendeesPerSlot === 'number'
        ? Math.max(1, Math.min(10000, Number(capacity.maxAttendeesPerSlot)))
        : 1;

  const weeklySchedule = isPlainObject(config.weeklySchedule) ? (config.weeklySchedule as Record<string, unknown>) : {};
  const getDaySchedule = (k: DayKey) => (isPlainObject(weeklySchedule[k]) ? (weeklySchedule[k] as Record<string, unknown>) : {});
  const hasAnyEnabledDay = DAY_KEYS.some((k) => getDaySchedule(k).enabled === true);

  const warnings = useMemo(() => {
    const out: string[] = [];
    if (!hasAnyEnabledDay) out.push('No availability set (all days disabled).');
    if (interval < duration) out.push('Slot interval is smaller than duration.');
    if (bookingMode === 'absolute' && absoluteStart && absoluteEnd && absoluteStart > absoluteEnd) {
      out.push('Booking window start date is after end date.');
    }
    // Light schedule validation
    for (const k of DAY_KEYS) {
      const ds = getDaySchedule(k);
      if (ds.enabled !== true) continue;
      const rangesRaw = Array.isArray(ds.ranges) ? (ds.ranges as unknown[]) : [];
      const ranges = rangesRaw
        .map((r) => (isPlainObject(r) ? (r as Record<string, unknown>) : null))
        .filter((r): r is Record<string, unknown> => Boolean(r))
        .map((r) => ({
          startMin: typeof r.start === 'string' ? parseHHmmToMinutes(r.start) : null,
          endMin: typeof r.end === 'string' ? parseHHmmToMinutes(r.end) : null,
        }))
        .filter((r): r is { startMin: number; endMin: number } => r.startMin !== null && r.endMin !== null)
        .sort((a, b) => a.startMin - b.startMin);
      for (const r of ranges) {
        if (r.startMin >= r.endMin) out.push(`${DAY_LABEL[k]} has a range with start ≥ end.`);
      }
      for (let i = 1; i < ranges.length; i++) {
        if (ranges[i].startMin < ranges[i - 1].endMin) {
          out.push(`${DAY_LABEL[k]} has overlapping ranges.`);
          break;
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(config), hasAnyEnabledDay, duration, interval, bookingMode]);

  const reschedulePaymentOptions = useMemo(() => {
    const pid = typeof config.reschedulePaymentFieldId === 'string' ? config.reschedulePaymentFieldId : '';
    const out = [...squarePaymentFields];
    if (pid && !out.some((x) => x.id === pid)) {
      out.unshift({ id: pid, label: `${pid.slice(0, 12)}… (missing payment field)` });
    }
    return out;
  }, [squarePaymentFields, config.reschedulePaymentFieldId]);

  const nextSlots = useMemo(() => {
    const out: string[] = [];
    if (!hasAnyEnabledDay) return out;
    const blackout = new Set((Array.isArray(config.blackoutDates) ? (config.blackoutDates as any[]) : []).map(String));
    const overrides = isPlainObject(config.dateOverrides) ? (config.dateOverrides as Record<string, unknown>) : {};

    const start = new Date();
    for (let dayOffset = 0; dayOffset <= 30 && out.length < 5; dayOffset++) {
      const day = addDays(start, dayOffset);
      if (day > horizonEnd) break;
      const iso = toDateOnlyString(day);
      if (blackout.has(iso)) continue;
      const override = isPlainObject(overrides[iso]) ? (overrides[iso] as Record<string, unknown>) : null;
      if (override?.closed === true) continue;

      const dk = getDayKeyFromDate(day);
      const ds = getDaySchedule(dk);
      if (ds.enabled !== true) continue;

      const baseRangesRaw =
        override && Array.isArray(override.ranges)
          ? (override.ranges as unknown[])
          : Array.isArray(ds.ranges)
            ? (ds.ranges as unknown[])
            : [];

      const ranges = baseRangesRaw
        .map((r) => (isPlainObject(r) ? (r as Record<string, unknown>) : null))
        .filter((r): r is Record<string, unknown> => Boolean(r))
        .map((r) => ({
          startMin: typeof r.start === 'string' ? parseHHmmToMinutes(r.start) : null,
          endMin: typeof r.end === 'string' ? parseHHmmToMinutes(r.end) : null,
        }))
        .filter((r): r is { startMin: number; endMin: number } => r.startMin !== null && r.endMin !== null)
        .sort((a, b) => a.startMin - b.startMin);

      for (const r of ranges) {
        for (let t = r.startMin; t + duration <= r.endMin && out.length < 5; t += interval) {
          const slot = new Date(day);
          slot.setHours(0, 0, 0, 0);
          slot.setMinutes(t);
          if (slot < new Date()) continue;
          out.push(format(slot, 'EEE MMM d, h:mm a'));
        }
        if (out.length >= 5) break;
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(config), duration, interval, horizonEnd.getTime(), hasAnyEnabledDay]);

  const applyMonFriNineToFive = () => {
    const next: Record<string, unknown> = { ...weeklySchedule };
    for (const k of DAY_KEYS) {
      next[k] = {
        enabled: k !== 'sat' && k !== 'sun',
        ranges: k !== 'sat' && k !== 'sun' ? [{ start: '09:00', end: '17:00' }] : [],
      };
    }
    onPatchConfig({ weeklySchedule: next });
  };

  const copyMondayToWeekdays = () => {
    const mon = getDaySchedule('mon');
    const next: Record<string, unknown> = { ...weeklySchedule };
    for (const k of ['tue', 'wed', 'thu', 'fri'] as DayKey[]) next[k] = { ...mon };
    onPatchConfig({ weeklySchedule: next });
  };

  const setDaySchedule = (k: DayKey, next: Record<string, unknown>) => {
    onPatchConfig({ weeklySchedule: { ...weeklySchedule, [k]: next } });
  };

  return (
    <div className="grid gap-3">
      <div className="rounded-xl border border-border/60 bg-muted/10 p-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Appointment summary</p>
            <p className="text-[11px] text-muted-foreground">
              {bookingMode === 'rolling'
                ? `Rolling window: next ${rollingDaysAhead} days`
                : `Absolute window: ${absoluteStart ? toDateOnlyString(absoluteStart) : '—'} → ${absoluteEnd ? toDateOnlyString(absoluteEnd) : '—'}`}
              {' · '}
              {duration}m/{interval}m
              {' · '}
              {capacityMode === 'group' ? `Group (${maxAttendees})` : '1:1'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!hasAnyEnabledDay ? <Badge variant="secondary">No hours</Badge> : <Badge variant="secondary">Hours set</Badge>}
            {warnings.length > 0 ? <Badge variant="destructive">Needs attention</Badge> : <Badge variant="secondary">OK</Badge>}
          </div>
        </div>

        {warnings.length > 0 ? (
          <div className="mt-3">
            <Alert variant="destructive">
              <AlertTitle>Warnings</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4">
                  {warnings.slice(0, 5).map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          </div>
        ) : null}

        <div className="mt-3 grid gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Next available slots</p>
          {nextSlots.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">No upcoming slots (set availability and slot rules).</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {nextSlots.map((s) => (
                <Badge key={s} variant="secondary">
                  {s}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border/60 bg-muted/10 p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Booking window</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label>Mode</Label>
            <Select
              value={bookingMode}
              onValueChange={(v) => {
                const prev = isPlainObject(config.bookingWindow) ? (config.bookingWindow as Record<string, unknown>) : {};
                onPatchConfig({ bookingWindow: { ...prev, mode: v } });
              }}
            >
              <SelectTrigger className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rolling">Rolling</SelectItem>
                <SelectItem value="absolute">Absolute</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {bookingMode === 'rolling' ? (
            <div className="grid gap-1">
              <Label>Rolling days ahead</Label>
              <Input
                type="number"
                min={1}
                max={730}
                value={rollingDaysAhead}
                onChange={(e) => {
                  const prev = isPlainObject(config.bookingWindow) ? (config.bookingWindow as Record<string, unknown>) : {};
                  const raw = e.target.value;
                  onPatchConfig({
                    bookingWindow: {
                      ...prev,
                      rollingDaysAhead: raw === '' ? undefined : Math.max(1, Math.min(730, Number.parseInt(raw, 10) || 365)),
                    },
                  });
                }}
              />
              <p className="text-[11px] text-muted-foreground">Bookable until: {toDateOnlyString(horizonEnd)}</p>
            </div>
          ) : (
            <div className="grid gap-1">
              <Label>Date range (absolute)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {absoluteStart && absoluteEnd
                      ? `${format(absoluteStart, 'PPP')} – ${format(absoluteEnd, 'PPP')}`
                      : absoluteStart
                        ? `${format(absoluteStart, 'PPP')} – …`
                        : 'Pick a date range'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    captionLayout="dropdown"
                    disabled={{ before: today }}
                    startMonth={today}
                    endMonth={horizonEnd}
                    selected={{ from: absoluteStart, to: absoluteEnd }}
                    onSelect={(range) => {
                      const prev = isPlainObject(config.bookingWindow) ? (config.bookingWindow as Record<string, unknown>) : {};
                      onPatchConfig({
                        bookingWindow: {
                          ...prev,
                          startDate: range?.from ? toDateOnlyString(range.from) : undefined,
                          endDate: range?.to ? toDateOnlyString(range.to) : undefined,
                        },
                      });
                    }}
                  />
                </PopoverContent>
              </Popover>
              <p className="text-[11px] text-muted-foreground">Future-only. UI capped at: {toDateOnlyString(horizonEnd)}</p>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border/60 bg-muted/10 p-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Availability (weekly)</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={applyMonFriNineToFive}>
              Apply Mon–Fri 9–5
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={copyMondayToWeekdays}>
              Copy Mon → Weekdays
            </Button>
          </div>
        </div>

        <div className="mt-2 grid gap-2">
          {DAY_KEYS.map((k) => {
            const ds = getDaySchedule(k);
            const enabled = ds.enabled === true;
            const rangesRaw = Array.isArray(ds.ranges) ? (ds.ranges as unknown[]) : [];
            const ranges = rangesRaw
              .map((r) => (isPlainObject(r) ? (r as Record<string, unknown>) : null))
              .filter((r): r is Record<string, unknown> => Boolean(r))
              .map((r) => ({
                start: typeof r.start === 'string' ? r.start : '',
                end: typeof r.end === 'string' ? r.end : '',
              }));

            return (
              <div key={k} className="rounded-lg border border-border/60 bg-background p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{DAY_LABEL[k]}</p>
                    <Badge variant="secondary" className="text-[10px]">
                      {enabled ? 'Enabled' : 'Off'}
                    </Badge>
                  </div>
                  <Switch checked={enabled} onCheckedChange={(checked) => setDaySchedule(k, { ...ds, enabled: checked })} />
                </div>

                {enabled ? (
                  <div className="mt-3 grid gap-2">
                    {ranges.length === 0 ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setDaySchedule(k, { ...ds, enabled: true, ranges: [{ start: '09:00', end: '17:00' }] })}
                      >
                        Add hours
                      </Button>
                    ) : (
                      <div className="grid gap-2">
                        {ranges.map((r, i) => (
                          <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                            <div className="grid gap-1">
                              <Label className="text-[11px]">Start</Label>
                              <Input
                                value={r.start}
                                className="font-mono text-xs"
                                placeholder="HH:mm"
                                onChange={(e) => {
                                  const next = [...ranges];
                                  next[i] = { ...next[i], start: e.target.value };
                                  setDaySchedule(k, { ...ds, ranges: next });
                                }}
                              />
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-[11px]">End</Label>
                              <Input
                                value={r.end}
                                className="font-mono text-xs"
                                placeholder="HH:mm"
                                onChange={(e) => {
                                  const next = [...ranges];
                                  next[i] = { ...next[i], end: e.target.value };
                                  setDaySchedule(k, { ...ds, ranges: next });
                                }}
                              />
                            </div>
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className="h-9 w-9"
                              aria-label="Remove range"
                              onClick={() => {
                                const next = ranges.filter((_, idx) => idx !== i);
                                setDaySchedule(k, { ...ds, ranges: next });
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setDaySchedule(k, { ...ds, ranges: [...ranges, { start: '09:00', end: '17:00' }] })}
                          >
                            Add range
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-border/60 bg-muted/10 p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Slot rules</p>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="grid gap-1">
            <Label>Duration (minutes)</Label>
            <Input
              type="number"
              min={5}
              max={1440}
              value={duration}
              onChange={(e) =>
                onPatchConfig({
                  slotDurationMinutes:
                    e.target.value === '' ? undefined : Math.max(5, Math.min(1440, Number.parseInt(e.target.value, 10) || 30)),
                })
              }
            />
          </div>
          <div className="grid gap-1">
            <Label>Interval (minutes)</Label>
            <Input
              type="number"
              min={5}
              max={1440}
              value={interval}
              onChange={(e) =>
                onPatchConfig({
                  slotIntervalMinutes:
                    e.target.value === '' ? undefined : Math.max(5, Math.min(1440, Number.parseInt(e.target.value, 10) || 30)),
                })
              }
            />
            {interval < duration ? (
              <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => onPatchConfig({ slotIntervalMinutes: duration })}>
                Fix: match interval to duration
              </Button>
            ) : null}
          </div>
          <div className="grid gap-1">
            <Label>Buffer before (minutes)</Label>
            <Input
              type="number"
              min={0}
              max={240}
              value={typeof config.bufferBeforeMinutes === 'number' ? Number(config.bufferBeforeMinutes) : 0}
              onChange={(e) =>
                onPatchConfig({
                  bufferBeforeMinutes:
                    e.target.value === '' ? undefined : Math.max(0, Math.min(240, Number.parseInt(e.target.value, 10) || 0)),
                })
              }
            />
          </div>
          <div className="grid gap-1">
            <Label>Buffer after (minutes)</Label>
            <Input
              type="number"
              min={0}
              max={240}
              value={typeof config.bufferAfterMinutes === 'number' ? Number(config.bufferAfterMinutes) : 0}
              onChange={(e) =>
                onPatchConfig({
                  bufferAfterMinutes:
                    e.target.value === '' ? undefined : Math.max(0, Math.min(240, Number.parseInt(e.target.value, 10) || 0)),
                })
              }
            />
          </div>
          <div className="grid gap-1">
            <Label>Min notice (minutes)</Label>
            <Input
              type="number"
              min={0}
              max={10080}
              value={typeof config.minNoticeMinutes === 'number' ? Number(config.minNoticeMinutes) : 0}
              onChange={(e) =>
                onPatchConfig({
                  minNoticeMinutes:
                    e.target.value === '' ? undefined : Math.max(0, Math.min(10080, Number.parseInt(e.target.value, 10) || 0)),
                })
              }
            />
          </div>
          <div className="grid gap-1">
            <Label>Slot generation horizon (days)</Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={typeof config.slotGenerationHorizonDays === 'number' ? Number(config.slotGenerationHorizonDays) : 90}
              onChange={(e) =>
                onPatchConfig({
                  slotGenerationHorizonDays:
                    e.target.value === '' ? undefined : Math.max(1, Math.min(365, Number.parseInt(e.target.value, 10) || 90)),
                })
              }
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border/60 bg-muted/10 p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attendance tracking</p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          When enabled on a published form, coordinators can mark <span className="font-medium">Attended</span> or{' '}
          <span className="font-medium">Not attended</span> for each confirmed booking after the scheduled slot starts (admin
          attendance list).
        </p>
        <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-background px-3 py-2">
          <Label htmlFor="track-attendance" className="cursor-pointer text-sm font-medium leading-snug">
            Track attendance for this appointment field
          </Label>
          <Switch
            id="track-attendance"
            checked={config.trackAttendance === true}
            onCheckedChange={(checked) => onPatchConfig({ trackAttendance: checked })}
          />
        </div>
      </div>

      <div className="rounded-xl border border-border/60 bg-muted/10 p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Capacity</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label>Mode</Label>
            <Select
              value={capacityMode}
              onValueChange={(v) => {
                const prev = isPlainObject(config.capacity) ? (config.capacity as Record<string, unknown>) : {};
                onPatchConfig({
                  capacity: {
                    ...prev,
                    mode: v,
                    maxAttendeesPerSlot: v === 'one_on_one' ? 1 : (prev.maxAttendeesPerSlot ?? 1),
                  },
                });
              }}
            >
              <SelectTrigger className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="one_on_one">One-on-one</SelectItem>
                <SelectItem value="group">Group</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label>Max attendees per slot</Label>
            <Input
              type="number"
              min={1}
              max={10000}
              disabled={capacityMode === 'one_on_one'}
              value={maxAttendees}
              onChange={(e) => {
                const prev = isPlainObject(config.capacity) ? (config.capacity as Record<string, unknown>) : {};
                onPatchConfig({
                  capacity: {
                    ...prev,
                    maxAttendeesPerSlot:
                      e.target.value === '' ? undefined : Math.max(1, Math.min(10000, Number.parseInt(e.target.value, 10) || 1)),
                  },
                });
              }}
            />
            {capacityMode === 'one_on_one' ? (
              <p className="text-[11px] text-muted-foreground">Locked to 1 for one-on-one.</p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border/60 bg-muted/10 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reschedule fee (Square)</p>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex rounded-full text-muted-foreground hover:text-foreground"
                aria-label="About reschedule fees"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm text-left" side="top">
              <p className="text-xs leading-relaxed">
                Charged when a logged-in student reschedules after a confirmed booking. The dollar amount is only{' '}
                <span className="font-medium">rescheduleFeeUsd</span> here — not the payment field&apos;s product amount. Add a Square{' '}
                <span className="font-medium">payment</span> field to this form and select it below. For reschedule-only forms, use{' '}
                <span className="font-medium">fixed amount 0</span> on that payment field.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          USD charged on reschedule; the server records the charge on the selected payment field.
        </p>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="reschedule-fee-usd">Fee (USD)</Label>
            <Input
              id="reschedule-fee-usd"
              type="number"
              min={0}
              max={10000}
              step={1}
              value={
                typeof config.rescheduleFeeUsd === 'number' && Number.isFinite(config.rescheduleFeeUsd)
                  ? config.rescheduleFeeUsd
                  : ''
              }
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  onPatchConfig({ rescheduleFeeUsd: undefined, reschedulePaymentFieldId: undefined });
                  return;
                }
                const n = Math.round(Number.parseFloat(raw));
                if (Number.isNaN(n)) return;
                const clamped = Math.max(0, Math.min(10000, n));
                onPatchConfig({ rescheduleFeeUsd: clamped });
              }}
              placeholder="0 = no fee"
              className="bg-background"
            />
          </div>
          <div className="grid gap-1">
            <Label>Square payment field</Label>
            <Select
              value={
                typeof config.reschedulePaymentFieldId === 'string' && config.reschedulePaymentFieldId
                  ? config.reschedulePaymentFieldId
                  : '__none'
              }
              onValueChange={(v) => {
                if (v === '__none') onPatchConfig({ reschedulePaymentFieldId: undefined });
                else onPatchConfig({ reschedulePaymentFieldId: v });
              }}
              disabled={
                typeof config.rescheduleFeeUsd !== 'number' ||
                !Number.isFinite(config.rescheduleFeeUsd) ||
                config.rescheduleFeeUsd <= 0
              }
            >
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Select payment field" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {reschedulePaymentOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {typeof config.rescheduleFeeUsd === 'number' && config.rescheduleFeeUsd > 0 && reschedulePaymentOptions.length === 0 ? (
              <p className="text-[11px] text-destructive">
                Add a <span className="font-medium">payment</span> field with gateway Square to this form.
              </p>
            ) : null}
          </div>
        </div>

        {crossFieldRescheduleWarning ? (
          <Alert variant="destructive" className="mt-3">
            <AlertTitle>Reschedule payment field</AlertTitle>
            <AlertDescription>{crossFieldRescheduleWarning}</AlertDescription>
          </Alert>
        ) : null}

        {typeof config.rescheduleFeeUsd === 'number' && config.rescheduleFeeUsd > 0 && !config.reschedulePaymentFieldId ? (
          <Alert variant="destructive" className="mt-3">
            <AlertTitle>Payment field required</AlertTitle>
            <AlertDescription>
              When the reschedule fee is greater than zero, select which Square payment field should receive the charge.
            </AlertDescription>
          </Alert>
        ) : null}
      </div>

      <Accordion type="multiple" className="rounded-xl border border-border/60 bg-muted/10 px-3">
        <AccordionItem value="exceptions">
          <AccordionTrigger>Exceptions</AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-3">
              <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Blackout dates</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Add blackout date
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      disabled={{ before: today }}
                      startMonth={today}
                      endMonth={horizonEnd}
                      onSelect={(d) => {
                        if (!d) return;
                        const iso = toDateOnlyString(d);
                        const prev = Array.isArray(config.blackoutDates) ? (config.blackoutDates as any[]) : [];
                        const next = Array.from(new Set(prev.map(String).concat([iso]))).sort();
                        onPatchConfig({ blackoutDates: next });
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(config.blackoutDates) ? (config.blackoutDates as any[]) : [])
                    .map(String)
                    .sort()
                    .map((d) => (
                      <Badge key={d} variant="secondary">
                        {d}
                      </Badge>
                    ))}
                </div>
              </div>

              <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Lunch breaks (advanced)</p>
                <Textarea
                  rows={4}
                  className="font-mono text-xs"
                  value={JSON.stringify(config.lunchBreaks ?? [], null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      onPatchConfig({ lunchBreaks: parsed });
                    } catch {
                      toast.error('Invalid lunchBreaks JSON');
                    }
                  }}
                />
              </div>

              <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date overrides</p>
                <Textarea
                  rows={5}
                  className="font-mono text-xs"
                  value={JSON.stringify(config.dateOverrides ?? {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      onPatchConfig({ dateOverrides: parsed });
                    } catch {
                      toast.error('Invalid dateOverrides JSON');
                    }
                  }}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="advanced">
          <AccordionTrigger>Advanced</AccordionTrigger>
          <AccordionContent>
            <div className="grid gap-3">
              <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Limits</p>
                <Textarea
                  rows={4}
                  className="font-mono text-xs"
                  value={JSON.stringify(config.limits ?? {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      onPatchConfig({ limits: parsed });
                    } catch {
                      toast.error('Invalid limits JSON');
                    }
                  }}
                />
              </div>
              <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Display</p>
                <Textarea
                  rows={4}
                  className="font-mono text-xs"
                  value={JSON.stringify(config.display ?? {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      onPatchConfig({ display: parsed });
                    } catch {
                      toast.error('Invalid display JSON');
                    }
                  }}
                />
              </div>
              <div className="grid gap-2 rounded-lg border border-border/60 bg-background p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Integrations</p>
                <Textarea
                  rows={4}
                  className="font-mono text-xs"
                  value={JSON.stringify(config.integrations ?? {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      onPatchConfig({ integrations: parsed });
                    } catch {
                      toast.error('Invalid integrations JSON');
                    }
                  }}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

